import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useActiveOrganization } from './useActiveOrganization';
import { useAuth } from './useAuth';
import { toast } from 'sonner';

export interface NotificationSchedule {
  id: string;
  organizationId: string;
  userId: string;
  transactionId?: string;
  patientName: string;
  patientPhone?: string;
  description?: string;
  totalAmount: number;
  installmentsCount: number;
  installmentValue: number;
  notificationDay: number;
  startDate: string;
  endDate?: string;
  isActive: boolean;
  currentInstallment: number;
  createdAt: string;
  updatedAt: string;
}

export interface CreateScheduleData {
  patientName: string;
  patientPhone?: string;
  description?: string;
  totalAmount: number;
  installmentsCount: number;
  notificationDay: number;
  startDate: Date;
  endDate?: Date;
  transactionId?: string;
  budgetId?: string;
}

// Helper function to format date without timezone conversion
const formatDateForDB = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

export function useNotificationSchedules() {
  const [schedules, setSchedules] = useState<NotificationSchedule[]>([]);
  const [loading, setLoading] = useState(true);
  const { activeOrganizationId } = useActiveOrganization();
  const { user } = useAuth();

  const fetchSchedules = async () => {
    if (!activeOrganizationId) {
      setSchedules([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('payment_notification_schedules')
        .select('*')
        .eq('organization_id', activeOrganizationId)
        .order('created_at', { ascending: false });

      if (error) throw error;

      const mapped: NotificationSchedule[] = (data || []).map(row => ({
        id: row.id,
        organizationId: row.organization_id,
        userId: row.user_id,
        transactionId: row.transaction_id || undefined,
        patientName: row.patient_name,
        patientPhone: row.patient_phone || undefined,
        description: row.description || undefined,
        totalAmount: Number(row.total_amount),
        installmentsCount: row.installments_count,
        installmentValue: Number(row.installment_value),
        notificationDay: row.notification_day,
        startDate: row.start_date,
        endDate: row.end_date || undefined,
        isActive: row.is_active,
        currentInstallment: row.current_installment,
        createdAt: row.created_at,
        updatedAt: row.updated_at,
      }));

      setSchedules(mapped);
    } catch (error) {
      console.error('Error fetching notification schedules:', error);
      toast.error('Erro ao carregar cobranças agendadas');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSchedules();
  }, [activeOrganizationId]);

  // Realtime subscription
  useEffect(() => {
    if (!activeOrganizationId) return;

    const channel = supabase
      .channel('notification-schedules-hook')
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'payment_notification_schedules',
        filter: `organization_id=eq.${activeOrganizationId}`
      }, () => {
        fetchSchedules();
      })
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [activeOrganizationId]);

  const createSchedule = async (data: CreateScheduleData) => {
    if (!activeOrganizationId || !user) {
      toast.error('Organização ou usuário não identificado');
      return;
    }

    try {
      const installmentValue = data.totalAmount / data.installmentsCount;

      const { error } = await supabase
        .from('payment_notification_schedules')
        .insert({
          organization_id: activeOrganizationId,
          user_id: user.id,
          transaction_id: data.transactionId || null,
          budget_id: data.budgetId || null,
          patient_name: data.patientName,
          patient_phone: data.patientPhone || null,
          description: data.description || null,
          total_amount: data.totalAmount,
          installments_count: data.installmentsCount,
          installment_value: installmentValue,
          notification_day: data.notificationDay,
          start_date: formatDateForDB(data.startDate),
          end_date: data.endDate ? formatDateForDB(data.endDate) : null,
        });

      if (error) throw error;

      toast.success('Cobrança agendada criada com sucesso');
      await fetchSchedules();
    } catch (error) {
      console.error('Error creating notification schedule:', error);
      toast.error('Erro ao criar cobrança agendada');
      throw error;
    }
  };

  const updateSchedule = async (id: string, data: Partial<CreateScheduleData>) => {
    try {
      const updateData: Record<string, unknown> = {};
      
      if (data.patientName !== undefined) updateData.patient_name = data.patientName;
      if (data.patientPhone !== undefined) updateData.patient_phone = data.patientPhone;
      if (data.description !== undefined) updateData.description = data.description;
      if (data.totalAmount !== undefined) updateData.total_amount = data.totalAmount;
      if (data.installmentsCount !== undefined) updateData.installments_count = data.installmentsCount;
      if (data.notificationDay !== undefined) updateData.notification_day = data.notificationDay;
      if (data.startDate !== undefined) updateData.start_date = formatDateForDB(data.startDate);
      if (data.endDate !== undefined) updateData.end_date = formatDateForDB(data.endDate);

      // Recalculate installment value if amount or count changed
      if (data.totalAmount !== undefined || data.installmentsCount !== undefined) {
        const schedule = schedules.find(s => s.id === id);
        const totalAmount = data.totalAmount ?? schedule?.totalAmount ?? 0;
        const installmentsCount = data.installmentsCount ?? schedule?.installmentsCount ?? 1;
        updateData.installment_value = totalAmount / installmentsCount;
      }

      updateData.updated_at = new Date().toISOString();

      const { error } = await supabase
        .from('payment_notification_schedules')
        .update(updateData)
        .eq('id', id);

      if (error) throw error;

      toast.success('Cobrança agendada atualizada');
      await fetchSchedules();
    } catch (error) {
      console.error('Error updating notification schedule:', error);
      toast.error('Erro ao atualizar cobrança agendada');
      throw error;
    }
  };

  const deleteSchedule = async (id: string) => {
    try {
      const { error } = await supabase
        .from('payment_notification_schedules')
        .delete()
        .eq('id', id);

      if (error) throw error;

      toast.success('Cobrança agendada excluída');
      await fetchSchedules();
    } catch (error) {
      console.error('Error deleting notification schedule:', error);
      toast.error('Erro ao excluir cobrança agendada');
      throw error;
    }
  };

  // Mark installment as notified - returns schedule info for further processing
  const markAsNotified = async (schedule: NotificationSchedule): Promise<{ success: boolean; isComplete: boolean }> => {
    try {
      const newInstallment = schedule.currentInstallment + 1;
      const isComplete = newInstallment >= schedule.installmentsCount;

      const { error } = await supabase
        .from('payment_notification_schedules')
        .update({
          current_installment: newInstallment,
          is_active: !isComplete,
          updated_at: new Date().toISOString(),
        })
        .eq('id', schedule.id);

      if (error) throw error;

      if (isComplete) {
        toast.success('Todas as parcelas foram notificadas! Cobrança concluída.');
      } else {
        toast.success(`Parcela ${newInstallment} de ${schedule.installmentsCount} marcada como notificada.`);
      }
      
      await fetchSchedules();
      return { success: true, isComplete };
    } catch (error) {
      console.error('Error marking installment as notified:', error);
      toast.error('Erro ao marcar parcela como notificada');
      return { success: false, isComplete: false };
    }
  };

  // Create a new schedule for remaining balance on the same day of next month
  const createScheduleForRemainingBalance = async (
    originalSchedule: NotificationSchedule,
    remainingBalance: number
  ) => {
    if (!activeOrganizationId || !user || remainingBalance <= 0) return;

    try {
      // Calculate next month's date with same day
      const today = new Date();
      const nextMonth = new Date(today.getFullYear(), today.getMonth() + 1, originalSchedule.notificationDay);
      
      // Handle edge case: if day doesn't exist in next month (e.g., day 31), use last day
      if (nextMonth.getDate() !== originalSchedule.notificationDay) {
        nextMonth.setDate(0); // Go to last day of intended month
      }

      const { error } = await supabase
        .from('payment_notification_schedules')
        .insert({
          organization_id: activeOrganizationId,
          user_id: user.id,
          transaction_id: originalSchedule.transactionId || null,
          budget_id: null, // Get from original if needed
          patient_name: originalSchedule.patientName,
          patient_phone: originalSchedule.patientPhone || null,
          description: originalSchedule.description || null,
          total_amount: remainingBalance,
          installments_count: 1,
          installment_value: remainingBalance,
          notification_day: originalSchedule.notificationDay,
          start_date: formatDateForDB(nextMonth),
          end_date: null,
          current_installment: 0,
          is_active: true,
        });

      if (error) throw error;

      toast.success('Nova notificação criada para o saldo restante no próximo mês.');
      await fetchSchedules();
    } catch (error) {
      console.error('Error creating schedule for remaining balance:', error);
      toast.error('Erro ao criar notificação para saldo restante');
    }
  };

  const toggleActive = async (id: string, isActive: boolean) => {
    try {
      const { error } = await supabase
        .from('payment_notification_schedules')
        .update({
          is_active: isActive,
          updated_at: new Date().toISOString(),
        })
        .eq('id', id);

      if (error) throw error;

      toast.success(isActive ? 'Cobrança reativada' : 'Cobrança pausada');
      await fetchSchedules();
    } catch (error) {
      console.error('Error toggling schedule active state:', error);
      toast.error('Erro ao alterar status da cobrança');
      throw error;
    }
  };

  // Check and update schedule when a transaction payment is made
  const checkAndUpdateScheduleForTransaction = async (
    transactionId: string, 
    newPaidAmount: number, 
    totalAmount: number
  ) => {
    try {
      // Find schedule linked to this transaction
      const { data: schedule, error: fetchError } = await supabase
        .from('payment_notification_schedules')
        .select('*')
        .eq('transaction_id', transactionId)
        .eq('is_active', true)
        .maybeSingle();

      if (fetchError) throw fetchError;
      if (!schedule) return; // No linked schedule

      // Calculate how many installments have been paid based on paid amount
      const installmentsPaid = Math.floor(newPaidAmount / schedule.installment_value);
      const isFullyPaid = newPaidAmount >= totalAmount;

      // Only update if there's a change
      if (installmentsPaid > schedule.current_installment || isFullyPaid) {
        const newCurrentInstallment = isFullyPaid 
          ? schedule.installments_count 
          : Math.min(installmentsPaid, schedule.installments_count);
        
        const newIsActive = newCurrentInstallment < schedule.installments_count;

        await supabase
          .from('payment_notification_schedules')
          .update({
            current_installment: newCurrentInstallment,
            is_active: newIsActive,
            updated_at: new Date().toISOString(),
          })
          .eq('id', schedule.id);

        console.log(`📋 Schedule updated: installment ${newCurrentInstallment}/${schedule.installments_count}, active: ${newIsActive}`);
      }
    } catch (error) {
      console.error('Error checking/updating schedule for transaction:', error);
    }
  };

  return {
    schedules,
    loading,
    createSchedule,
    updateSchedule,
    deleteSchedule,
    markAsNotified,
    toggleActive,
    createScheduleForRemainingBalance,
    checkAndUpdateScheduleForTransaction,
    refreshSchedules: fetchSchedules,
  };
}
